import React, { useMemo, useState, Component } from 'react';
import { ComponentType } from 'react';
import { useData } from '../../store/DataContext';
import { AlertCircleIcon, BarChart3Icon, CheckSquareIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, CreditCardIcon, FileEditIcon, FileSpreadsheetIcon, FileTextIcon, LayoutDashboardIcon, ListIcon, MenuIcon, PlusIcon, ReceiptIcon, SettingsIcon, UsersIcon, Undo2Icon, XIcon } from 'lucide-react';
import { BillingDashboard } from './BillingDashboard';
import { Customers } from './Customers';
import { ItemList } from './ItemList';
import { Invoices } from './Invoices';
import { NewInvoice } from './NewInvoice';
import { Payments } from './Payments';
import { Quotations } from './Quotations';
import { CreditNotes } from './CreditNotes';
import { Reports } from './Reports';
import { Settings } from './Settings';
export type BillingSection = 'dashboard' | 'customers' | 'new-invoice' | 'invoices-draft' | 'invoices-sent' | 'invoices-unpaid' | 'invoices-paid' | 'invoices-partial' | 'invoices-overdue' | 'quotations' | 'receipts' | 'payments' | 'credit-notes' | 'item-list' | 'reports' | 'settings';
type NavigationItem = {
  id: BillingSection;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  count?: number;
};
type NavigationGroup = {
  label: string;
  items: NavigationItem[];
};
const sectionCopy: Record<BillingSection, {
  title: string;
  subtitle: string;
}> = {
  dashboard: {
    title: 'Billing overview',
    subtitle: 'Monitor collections, receivables, and today’s billing priorities.'
  },
  customers: {
    title: 'Customers',
    subtitle: 'Find registered patients or manage walk-in billing profiles.'
  },
  'new-invoice': {
    title: 'Create invoice',
    subtitle: 'Build a bill from the service catalogue or an active patient visit.'
  },
  'invoices-draft': {
    title: 'Draft invoices',
    subtitle: 'Review invoices before sending them to customers.'
  },
  'invoices-sent': {
    title: 'Sent invoices',
    subtitle: 'Track invoices that have been issued to customers.'
  },
  'invoices-unpaid': {
    title: 'Unpaid invoices',
    subtitle: 'Follow up invoices with an outstanding balance.'
  },
  'invoices-paid': {
    title: 'Paid invoices',
    subtitle: 'Review fully settled billing records.'
  },
  'invoices-partial': {
    title: 'Partially paid invoices',
    subtitle: 'Track invoices with a remaining balance.'
  },
  'invoices-overdue': {
    title: 'Overdue invoices',
    subtitle: 'Prioritize accounts that are past their due date.'
  },
  quotations: {
    title: 'Quotations',
    subtitle: 'Prepare and manage estimates before issuing an invoice.'
  },
  receipts: {
    title: 'Receipts',
    subtitle: 'View and issue payment receipts for collected balances.'
  },
  payments: {
    title: 'Payments received',
    subtitle: 'Record payments and keep invoice balances current.'
  },
  'credit-notes': {
    title: 'Credit notes',
    subtitle: 'Manage billing corrections, refunds, and service credits.'
  },
  'item-list': {
    title: 'Service catalogue',
    subtitle: 'Maintain the approved billable services and pricing list.'
  },
  reports: {
    title: 'Reports',
    subtitle: 'Review financial activity across billing operations.'
  },
  settings: {
    title: 'Billing settings',
    subtitle: 'Configure billing preferences and operational controls.'
  }
};
export function BillingModule() {
  const {
    currentUser,
    invoices
  } = useData();
  const [activeSection, setActiveSection] = useState<BillingSection>('dashboard');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const invoiceCounts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      draft: invoices.filter(invoice => invoice.status === 'Draft').length,
      sent: invoices.filter(invoice => invoice.status === 'Sent').length,
      unpaid: invoices.filter(invoice => invoice.status === 'Unpaid').length,
      partial: invoices.filter(invoice => invoice.status === 'Partially Paid').length,
      paid: invoices.filter(invoice => invoice.status === 'Paid').length,
      overdue: invoices.filter(invoice => invoice.status === 'Overdue' || (invoice.status === 'Unpaid' || invoice.status === 'Partially Paid') && invoice.dueDate < today).length
    };
  }, [invoices]);
  if (!currentUser || currentUser.role !== 'Administrator' && currentUser.role !== 'Reception') {
    return <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-700" data-id="element-974">
        You do not have permission to access the Billing Module.
      </div>;
  }
  const isAdmin = currentUser.role === 'Administrator';
  const navigationGroups: NavigationGroup[] = [{
    label: 'Overview',
    items: [{
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboardIcon
    }]
  }, {
    label: 'Sales',
    items: [{
      id: 'customers',
      label: 'Customers',
      icon: UsersIcon
    }, {
      id: 'new-invoice',
      label: 'New Invoice',
      icon: PlusIcon
    }, {
      id: 'quotations',
      label: 'Quotations',
      icon: FileSpreadsheetIcon
    }, {
      id: 'credit-notes',
      label: 'Credit Notes',
      icon: Undo2Icon
    }]
  }, {
    label: 'Invoices',
    items: [{
      id: 'invoices-draft',
      label: 'Draft',
      icon: FileEditIcon,
      count: invoiceCounts.draft
    }, {
      id: 'invoices-sent',
      label: 'Sent',
      icon: FileTextIcon,
      count: invoiceCounts.sent
    }, {
      id: 'invoices-unpaid',
      label: 'Unpaid',
      icon: AlertCircleIcon,
      count: invoiceCounts.unpaid
    }, {
      id: 'invoices-partial',
      label: 'Partially Paid',
      icon: ClockIcon,
      count: invoiceCounts.partial
    }, {
      id: 'invoices-paid',
      label: 'Paid',
      icon: CheckSquareIcon,
      count: invoiceCounts.paid
    }, {
      id: 'invoices-overdue',
      label: 'Overdue',
      icon: AlertCircleIcon,
      count: invoiceCounts.overdue
    }]
  }, {
    label: 'Cashflow',
    items: [{
      id: 'payments',
      label: 'Payments Received',
      icon: CreditCardIcon
    }, {
      id: 'receipts',
      label: 'Receipts',
      icon: ReceiptIcon
    }]
  }, {
    label: 'Admin',
    items: [...(isAdmin ? [{
      id: 'item-list' as BillingSection,
      label: 'Item List',
      icon: ListIcon
    }] : []), {
      id: 'reports',
      label: 'Reports',
      icon: BarChart3Icon
    }, ...(isAdmin ? [{
      id: 'settings' as BillingSection,
      label: 'Settings',
      icon: SettingsIcon
    }] : [])]
  }];
  const selectSection = (section: BillingSection) => {
    setActiveSection(section);
    setIsMobileNavOpen(false);
  };
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <BillingDashboard onNavigate={selectSection} data-id="element-975" />;
      case 'customers':
        return <Customers data-id="element-976" />;
      case 'item-list':
        return <ItemList data-id="element-977" />;
      case 'new-invoice':
        return <NewInvoice onNavigate={selectSection} data-id="element-978" />;
      case 'invoices-draft':
      case 'invoices-sent':
      case 'invoices-unpaid':
      case 'invoices-paid':
      case 'invoices-partial':
      case 'invoices-overdue':
        return <Invoices filter={activeSection} onNavigate={selectSection} data-id="element-979" />;
      case 'payments':
      case 'receipts':
        return <Payments data-id="element-980" />;
      case 'quotations':
        return <Quotations onNavigate={selectSection} data-id="element-981" />;
      case 'credit-notes':
        return <CreditNotes onNavigate={selectSection} data-id="element-982" />;
      case 'reports':
        return <Reports data-id="element-983" />;
      case 'settings':
        return <Settings data-id="element-984" />;
    }
  };
  const navigation = (mobile = false) => <nav className="space-y-5" aria-label="Billing navigation" data-id="element-985">
      {navigationGroups.map(group => <div key={group.label} data-id="element-986">
          <p className={`mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 ${isCollapsed && !mobile ? 'sr-only' : ''}`} data-id="element-987">
            {group.label}
          </p>
          <div className="space-y-0.5" data-id="element-988">
            {group.items.map(item => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return <button key={item.id} type="button" onClick={() => selectSection(item.id)} title={isCollapsed && !mobile ? item.label : undefined} className={`group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'} ${isCollapsed && !mobile ? 'justify-center px-2' : ''}`} data-id="element-989">
                  <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'} ${isCollapsed && !mobile ? '' : 'mr-3'}`} data-id="element-990" />
                  {(!isCollapsed || mobile) && <>
                      <span className="truncate" data-id="element-991">{item.label}</span>
                      {item.count !== undefined && item.count > 0 && <span className={`ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold ${isActive ? 'bg-blue-100 text-blue-700' : item.id === 'invoices-overdue' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-992">
                          {item.count}
                        </span>}
                    </>}
                </button>;
        })}
          </div>
        </div>)}
    </nav>;
  const copy = sectionCopy[activeSection];
  const showNewInvoiceAction = activeSection !== 'new-invoice';
  return <div className="-m-4 flex min-h-[calc(100vh-4rem)] bg-slate-50 sm:-m-6 lg:-m-8" data-id="element-993">
      <aside className={`hidden shrink-0 border-r border-slate-200 bg-white lg:flex lg:flex-col ${isCollapsed ? 'w-[76px]' : 'w-[264px]'} transition-[width] duration-200`} data-id="element-994">
        <div className={`border-b border-slate-100 ${isCollapsed ? 'p-3' : 'p-5'}`} data-id="element-995">
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`} data-id="element-996">
            <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Care" className="h-9 w-9 rounded-lg object-contain ring-1 ring-slate-100" data-id="element-997" />
            {!isCollapsed && <div className="min-w-0" data-id="element-998">
                <p className="truncate text-sm font-bold text-slate-900" data-id="element-999">
                  BMED Care
                </p>
                <p className="truncate text-xs font-medium text-pink-600" data-id="element-1000">
                  Billing &amp; Invoicing
                </p>
              </div>}
          </div>
          {!isCollapsed && <div className="mt-4 inline-flex items-center rounded-md border border-blue-100 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700" data-id="element-1001">
              Reception workspace
            </div>}
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-5" data-id="element-1002">{navigation()}</div>

        <div className="border-t border-slate-100 p-3" data-id="element-1003">
          {!isCollapsed && <div className="mb-3 rounded-lg bg-slate-50 px-3 py-2" data-id="element-1004">
              <p className="truncate text-xs font-semibold text-slate-700" data-id="element-1005">
                {currentUser.fullName}
              </p>
              <p className="text-[11px] text-slate-500" data-id="element-1006">Live billing access</p>
            </div>}
          <button type="button" onClick={() => setIsCollapsed(value => !value)} className="flex w-full items-center justify-center rounded-md py-2 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={isCollapsed ? 'Expand billing navigation' : 'Collapse billing navigation'} data-id="element-1007">
            {isCollapsed ? <ChevronRightIcon className="h-4 w-4" data-id="element-1008" /> : <ChevronLeftIcon className="h-4 w-4" data-id="element-1009" />}
          </button>
        </div>
      </aside>

      {isMobileNavOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-id="element-1010">
          <button type="button" aria-label="Close billing navigation" className="absolute inset-0 bg-slate-950/35" onClick={() => setIsMobileNavOpen(false)} data-id="element-1011" />
          <aside className="relative flex h-full w-[280px] flex-col bg-white shadow-2xl" data-id="element-1012">
            <div className="flex items-center justify-between border-b border-slate-100 p-5" data-id="element-1013">
              <div className="flex items-center gap-3" data-id="element-1014">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Care" className="h-9 w-9 rounded-lg object-contain ring-1 ring-slate-100" data-id="element-1015" />
                <div data-id="element-1016">
                  <p className="text-sm font-bold text-slate-900" data-id="element-1017">BMED Care</p>
                  <p className="text-xs font-medium text-pink-600" data-id="element-1018">
                    Billing &amp; Invoicing
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setIsMobileNavOpen(false)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Close billing navigation" data-id="element-1019">
                <XIcon className="h-5 w-5" data-id="element-1020" />
              </button>
            </div>
            <div className="px-5 pt-4" data-id="element-1021">
              <span className="inline-flex rounded-md border border-blue-100 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700" data-id="element-1022">
                Reception workspace
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-1023">{navigation(true)}</div>
          </aside>
        </div>}

      <section className="min-w-0 flex-1" data-id="element-1024">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 lg:px-8" data-id="element-1025">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4" data-id="element-1026">
            <div className="flex min-w-0 items-center gap-3" data-id="element-1027">
              <button type="button" onClick={() => setIsMobileNavOpen(true)} className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" aria-label="Open billing navigation" data-id="element-1028">
                <MenuIcon className="h-5 w-5" data-id="element-1029" />
              </button>
              <div className="min-w-0" data-id="element-1030">
                <p className="mb-0.5 text-xs font-medium text-slate-500" data-id="element-1031">
                  Reception <span className="mx-1 text-slate-300" data-id="element-1032">/</span>{' '}
                  Billing
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-1033">
                  <h1 className="truncate text-lg font-bold text-slate-900 sm:text-xl" data-id="element-1034">
                    {copy.title}
                  </h1>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-id="element-1035">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-1036" />{' '}
                    Live data
                  </span>
                </div>
                <p className="hidden text-sm text-slate-500 xl:block" data-id="element-1037">
                  {copy.subtitle}
                </p>
              </div>
            </div>
            {showNewInvoiceAction && <button type="button" onClick={() => selectSection('new-invoice')} className="inline-flex shrink-0 items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-1038">
                <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-1039" />
                <span className="hidden sm:inline" data-id="element-1040">New Invoice</span>
                <span className="sm:hidden" data-id="element-1041">Invoice</span>
              </button>}
          </div>
        </header>

        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8" data-id="element-1042">
          {renderContent()}
        </main>
      </section>
    </div>;
}